import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Photo } from '../../types/types';
import { useAnniversary } from '../../context/AnniversaryContext';

interface CollagePhotoGalleryProps {
  onLightboxChange?: (isOpen: boolean) => void;
}

const CollagePhotoGallery: React.FC<CollagePhotoGalleryProps> = ({ onLightboxChange }) => {
  const { data } = useAnniversary();
  const [currentPhoto, setCurrentPhoto] = useState<Photo | null>(null);
  
  // Parallax scrolling effect
  const { scrollY } = useScroll();
  
  // Create transform values outside of callbacks
  // We'll create a fixed number of transform values with different speeds
  const y1 = useTransform(scrollY, [0, 2000], [0, 100]); // slow
  const y2 = useTransform(scrollY, [0, 2000], [0, 200]); // medium
  const y3 = useTransform(scrollY, [0, 2000], [0, 300]); // fast
  const y4 = useTransform(scrollY, [0, 2000], [0, 400]); // faster
  const y5 = useTransform(scrollY, [0, 2000], [0, 500]); // fastest
  
  // Function to get parallax y value based on index
  const getParallaxY = (index: number) => {
    // Cycle through the 5 different speeds
    switch (index % 5) {
      case 0: return y1;
      case 1: return y2;
      case 2: return y3;
      case 3: return y4;
      case 4: return y5;
      default: return y1;
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Open photo in lightbox
  const openLightbox = (photo: Photo) => {
    setCurrentPhoto(photo);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when lightbox is open
    onLightboxChange?.(true); // Notify parent that lightbox is open
  };
  
  // Close lightbox
  const closeLightbox = useCallback(() => {
    setCurrentPhoto(null);
    document.body.style.overflow = ''; // Restore scrolling
    onLightboxChange?.(false); // Notify parent that lightbox is closed
  }, [onLightboxChange]);
  
  // Navigate to previous photo
  const prevPhoto = useCallback(() => {
    if (!currentPhoto) return;
    
    const currentIndex = data.photos.findIndex(photo => photo.id === currentPhoto.id);
    const prevIndex = currentIndex <= 0 ? data.photos.length - 1 : currentIndex - 1;
    setCurrentPhoto(data.photos[prevIndex]);
  }, [currentPhoto, data.photos]);
  
  // Navigate to next photo
  const nextPhoto = useCallback(() => {
    if (!currentPhoto) return;
    
    const currentIndex = data.photos.findIndex(photo => photo.id === currentPhoto.id);
    const nextIndex = currentIndex >= data.photos.length - 1 ? 0 : currentIndex + 1;
    setCurrentPhoto(data.photos[nextIndex]);
  }, [currentPhoto, data.photos]);
  
  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!currentPhoto) return;
      
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          prevPhoto();
          break;
        case 'ArrowRight':
          nextPhoto();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPhoto, prevPhoto, nextPhoto, closeLightbox]);

  // Random position generator for collage effect
  const getRandomPosition = (index: number) => {
    // Create tighter grid with more columns for better packing
    const cols = 6; // More columns for tighter layout
    
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    // Tighter base grid position - reduced spacing
    const baseLeft = (col * 16) + (col * 2); // 16% width + 2% spacing (much tighter)
    const baseTop = (row * 20) + (row * 2); // 20% height + 2% spacing (much tighter)
    
    // Add more randomness (±12% max) for organic scattered look
    const randomOffsetX = (Math.sin(index * 2.3) * 12); // Increased randomness
    const randomOffsetY = (Math.cos(index * 1.7) * 12);
    
    // Keep photos away from header (title + menu) - start at 15% top minimum
    // Also avoid the right side where the menu is (max 78% left to avoid menu area)
    const left = Math.max(2, Math.min(78, baseLeft + randomOffsetX)); // Avoid menu on right
    const top = Math.max(15, Math.min(80, baseTop + randomOffsetY)); // Clear header area
    
    // Varied rotation for more dynamic look
    const rotations = [-12, -8, -5, -3, 0, 3, 5, 8, 12, -10, 10, -6, 6, -4, 4, -2, 2];
    const rotate = rotations[index % rotations.length];
    
    return { top: `${top}%`, left: `${left}%`, rotate };
  };
  
  // Generate random size for collage effect
  const getRandomSize = (index: number) => {
    const sizes = ['small', 'medium', 'large', 'extra-large'];
    return sizes[index % sizes.length];
  };
  
  return (
    <CollageSection id="gallery">
      <CollageOverlay>
        <CollageTitleContainer>
          <CollageTitle
            as={motion.h1}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our Memories
          </CollageTitle>
        </CollageTitleContainer>
      </CollageOverlay>
      <CollageContainer>
        {data.photos.map((photo, index) => {
          const position = getRandomPosition(index);
          const sizeClass = getRandomSize(index);
          const parallaxY = getParallaxY(index);
          
          return (
            <CollagePhoto
              key={photo.id}
              layoutId={photo.id}
              style={{
                top: position.top,
                left: position.left,
                rotate: position.rotate,
                y: parallaxY,
              }}
              sizeClass={sizeClass}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                transition: { 
                  duration: 0.6, 
                  delay: index * 0.05
                }
              }}
              whileHover={{ 
                scale: 1.1, 
                zIndex: 999,
                boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
                rotate: 0,
                transition: { duration: 0.3 }
              }}
              onClick={() => openLightbox(photo)}
            >
              <img src={photo.path} alt={photo.caption} />
            </CollagePhoto>
          );
        })}
      </CollageContainer>
      
      {/* Lightbox */}
      <AnimatePresence>
        {currentPhoto && (
          <Lightbox
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <LightboxContent
              as={motion.div}
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking content
            >
              <LightboxClose onClick={closeLightbox}>&times;</LightboxClose>
              <LightboxNavButton className="prev" onClick={prevPhoto}>&lt;</LightboxNavButton>
              <LightboxNavButton className="next" onClick={nextPhoto}>&gt;</LightboxNavButton>
              
              <LightboxImageContainer>
                <LightboxImage src={currentPhoto.path} alt={currentPhoto.caption} />
              </LightboxImageContainer>
              
              <LightboxCaption>
                <h3>{currentPhoto.caption}</h3>
                <p>{formatDate(currentPhoto.date)}</p>
                {currentPhoto.location && <p className="location">{currentPhoto.location}</p>}
              </LightboxCaption>
            </LightboxContent>
          </Lightbox>
        )}
      </AnimatePresence>
    </CollageSection>
  );
};

const CollageSection = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  padding: 0;
  margin: 0;
  overflow: auto;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  
  /* Hide scrollbar for all browsers */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, and Opera */
  }
`;

const CollageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(26, 26, 46, 0.95) 0%,
    rgba(26, 26, 46, 0.8) 70%,
    transparent 100%
  );
`;

const CollageTitleContainer = styled.div`
  text-align: center;
  padding-top: 1rem;
`;

const CollageTitle = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 800;
  background: ${({ theme }) => theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const CollageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200vh;
  padding: 100px 0;
`;

const CollagePhoto = styled(motion.div)<{ sizeClass: string }>`
  position: absolute;
  background-color: white;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 8px;
  cursor: pointer;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.9);
  
  /* Size variations - more varied and overlapping */
  ${props => {
    switch(props.sizeClass) {
      case 'small':
        return `
          width: 180px;
          height: 140px;
          z-index: 3;
        `;
      case 'medium':
        return `
          width: 220px;
          height: 180px;
          z-index: 2;
        `;
      case 'large':
        return `
          width: 280px;
          height: 220px;
          z-index: 4;
        `;
      case 'extra-large':
        return `
          width: 320px;
          height: 260px;
          z-index: 1;
        `;
      default:
        return `
          width: 220px;
          height: 180px;
          z-index: 2;
        `;
    }
  }}
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.borderRadius.small};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    ${props => {
      switch(props.sizeClass) {
        case 'small':
          return `width: 140px; height: 110px;`;
        case 'medium':
          return `width: 170px; height: 140px;`;
        case 'large':
          return `width: 210px; height: 170px;`;
        case 'extra-large':
          return `width: 240px; height: 200px;`;
        default:
          return `width: 170px; height: 140px;`;
      }
    }}
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    ${props => {
      switch(props.sizeClass) {
        case 'small':
          return `width: 110px; height: 90px;`;
        case 'medium':
          return `width: 140px; height: 110px;`;
        case 'large':
          return `width: 170px; height: 140px;`;
        case 'extra-large':
          return `width: 190px; height: 160px;`;
        default:
          return `width: 140px; height: 110px;`;
      }
    }}
  }
`;

const Lightbox = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
`;

const LightboxContent = styled(motion.div)`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
`;

const LightboxImageContainer = styled.div`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.large};
`;

const LightboxImage = styled.img`
  width: 100%;
  height: 70vh;
  object-fit: contain;
  background-color: rgba(0, 0, 0, 0.8);
`;

const LightboxCaption = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
  
  h3 {
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
  
  p {
    margin: 0;
    opacity: 0.8;
  }
  
  .location {
    margin-top: ${({ theme }) => theme.spacing.xs};
    font-style: italic;
  }
`;

const LightboxClose = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.light};
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 10;
`;

const LightboxNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.light};
  background: rgba(0, 0, 0, 0.3);
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  
  &.prev {
    left: -70px;
  }
  
  &.next {
    right: -70px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    &.prev {
      left: -20px;
    }
    
    &.next {
      right: -20px;
    }
  }
`;

export default CollagePhotoGallery; 